import { config, renderTokens } from "@/config/eventConfig";
import { usePersistentState } from "@/hooks/usePersistentState";

const DATA_STANDING =
  "If this touches real names or records, build it on a made-up example with the same fields instead. That is not a lesser version. It is the one you are allowed to use next week.";
const DATA_STANDING_SHEET =
  "This recipe is always built on a made-up sheet. Never upload a real one: rebuild it with the same column headings and about ten invented rows. That is not a lesser version. It is the one you are allowed to use next week.";

// A recipe as one readable reference page, top to bottom. No stepper, no
// inputs, no completion tracking. Guidance only.
export const RecipeEngine = () => {
  const [recipeId] = usePersistentState("build.recipeId", null);
  const recipe = recipeId ? config.recipeDocs?.[recipeId] : null;

  const render = (text) => {
    if (typeof text !== "string") return text;
    return renderTokens(
      text
        .split("{workspace.name}")
        .join("your workspace")
        .split("{workspace.surfaces}")
        .join("your workspace's tools")
    );
  };

  if (!recipeId) {
    return (
      <div data-testid="recipe-reference-empty" className="bg-white border border-sand p-8">
        <p className="text-ink/60">
          Pick a recipe on the previous step, then come back here to read its
          guide.
        </p>
      </div>
    );
  }
  if (!recipe) {
    return (
      <div data-testid="recipe-reference-coming-soon" className="bg-white border border-sand p-8">
        <p className="text-ink/60">This recipe's guide is coming soon.</p>
      </div>
    );
  }

  const steps = recipe.steps || [];
  const hasStructureOnly = steps.some((s) => s.structure_only);
  const isSheet = recipe.id === "spreadsheet-question";
  const showDataCallout = hasStructureOnly || isSheet;
  const allStuck = steps.flatMap((s) => [
    ...(s.stuck || []),
    ...((s.structure_only && s.structure_only.stuck) || []),
  ]);

  return (
    <div data-testid="recipe-reference">
      {/* Header */}
      <h2 data-testid="recipe-title" className="font-display text-3xl leading-none mb-2">
        {recipe.title}
      </h2>
      <p className="text-ink/70 mb-2">{render(recipe.description)}</p>
      <div className="flex flex-wrap gap-x-6 gap-y-1 mb-8">
        <span className="mono-label text-ink/50">{recipe.minutes} MINUTES</span>
        {recipe.tools?.length > 0 && (
          <span className="mono-label text-ink/50">
            Tools: {recipe.tools.map((t) => render(t)).join(", ")}
          </span>
        )}
      </div>

      {recipe.scope_promise && (
        <div data-testid="recipe-scope-promise" className="callout-guidance text-teal mb-6">
          <p className="mono-label text-teal mb-2">What a win looks like</p>
          <p className="text-ink/80">{render(recipe.scope_promise)}</p>
        </div>
      )}

      {showDataCallout && (
        <div data-testid="recipe-data-callout" className="callout-warning mb-6">
          <p className="mono-label text-orange-dark mb-2">On real data</p>
          <p className="text-ink/80">{isSheet ? DATA_STANDING_SHEET : DATA_STANDING}</p>
        </div>
      )}

      {recipe.card_note && (
        <p data-testid="recipe-card-note" className="callout-guidance text-teal mb-6">
          {render(recipe.card_note)}
        </p>
      )}

      {/* Steps as a numbered reference list */}
      <ol data-testid="recipe-steps" className="mt-10 space-y-8">
        {steps.map((s, i) => {
          const action = s.structure_only?.participant_action
            ? `${render(s.participant_action)} ${render(s.structure_only.participant_action)}`
            : render(s.participant_action);
          return (
            <li key={i} className="border-t border-sand pt-6">
              <div className="flex gap-4">
                <span className="w-8 h-8 shrink-0 rounded-full bg-teal text-white font-display text-lg leading-none flex items-center justify-center">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 data-testid={`step-title-${i}`} className="font-display text-2xl leading-none mb-1">
                    {s.title}
                  </h3>
                  {s.minutes && (
                    <p className="mono-label text-ink/40 mb-4">about {s.minutes} minutes</p>
                  )}
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
                    <div className="min-w-0">
                      <p className="text-ink mb-3">{action}</p>
                      <p className="italic text-ink/70 border-l-2 border-sand pl-4">
                        {render(s.participant_example)}
                      </p>
                      {s.note && (
                        <p className="text-sm text-ink/50 mt-3">{render(s.note)}</p>
                      )}
                      {s.callout && (
                        <div className="callout-guidance text-teal mt-3">
                          {render(s.callout)}
                        </div>
                      )}
                      {s.warning && (
                        <div className="callout-warning mt-3">
                          <p className="text-ink/80">{render(s.warning)}</p>
                        </div>
                      )}
                    </div>
                    <aside
                      data-testid={`volunteer-move-${i}`}
                      className="border-l-[3px] border-teal pl-4 h-fit"
                    >
                      <p className="mono-label text-teal mb-2">VOLUNTEER MOVE</p>
                      <p className="text-sm text-ink/70">{render(s.volunteer_move)}</p>
                    </aside>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {/* One stuck block at the foot */}
      {allStuck.length > 0 && (
        <div data-testid="stuck-panel" className="callout-warning mt-10">
          <p className="mono-label text-orange-dark mb-2">If you get stuck</p>
          <ul className="list-disc pl-5 space-y-1 text-ink/70 text-sm">
            {allStuck.map((b, i) => (
              <li key={i}>{render(b)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
